import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../../../customComponents/Footer";
import { ProfileContext } from "../../../context/ProfileProvider";
import "./infrastructure.css";

export default function Infrastructure() {
  const navigate = useNavigate();
  const { newStoredTheme } = useContext(ProfileContext) || {};
  const isLight = newStoredTheme === "light" || (typeof document !== "undefined" && document.body.classList.contains("light_theme"));

  return (
    <div className="infrastructure-page-wrapper">
      <Helmet>
        <title>Settlement Infrastructure | AGCX</title>
        <meta
          name="description"
          content="Settlement infrastructure, not a payment button. AGCX assigns your institution a dedicated IBAN for AED deposits, free."
        />
        <meta
          name="keywords"
          content="crypto exchange UAE, institutional settlement, dedicated IBAN, AED deposits, AGCX"
        />
        <link rel="canonical" href="https://arabglobal.ae/infrastructure" />
      </Helmet>

      {/* 1. HERO SECTION */}
      <section
        id="hero"
        className="inst-hero-section"
      >
        <div className="inst-hero-container">
          {/* Left Column Content */}
          <div className="inst-hero-content">
            <h1 className="inst-hero-title">
              Settlement infrastructure,<br />
              not a payment button.
            </h1>
            <p className="inst-hero-description">
              AGCX assigns your institution a dedicated IBAN for AED deposits, free. Every transfer is identified and credited automatically, withdrawals settle same-day (weekends included), and client money is held in accounts kept separate from ours.
            </p>
            <div className="inst-hero-actions">
              <button className="inst-btn-primary" onClick={() => navigate("/signup")}>
                Talk to the institutional desk &rarr;
              </button>
              <button className="inst-btn-secondary" onClick={() => navigate("/contact")}>
                Request onboarding pack
              </button>
            </div>
          </div>

          {/* Right Column Isometric 3D Graphic */}
          <div className="inst-hero-graphic">
            <img
              src={isLight ? "/images/infrastructure-img/infrastructure_hero_img_light.png" : "/images/infrastructure-img/infrastructure_hero_img.png"}
              alt="Settlement Infrastructure Banking Gateway"
              className="inst-hero-img"
            />
          </div>
        </div>
      </section>

      {/* 2. WHY INSTITUTIONS CHOOSE AGCX SECTION */}
      <section
        id="why-agcx"
        className="inst-why-section"
      >
        <div className="inst-why-container">
          <h2 className="inst-why-title">
            Why institutions choose <span className="inst-why-gold">AgcX</span>
          </h2>

          <div className="inst-why-grid">
            {/* CARD 1 */}
            <div className="inst-why-card">
              <img src="/images/infrastructure-img/why_cards_back.png" alt="" className="inst-why-card-pattern" />
              <div className="inst-why-badge-wrap">
                <img
                  src={isLight ? "/images/infrastructure-img/why_institutions_vector_light.png" : "/images/infrastructure-img/why_institutions_vector.png"}
                  alt="Separate Accounts"
                  className="inst-why-vector-img"
                />
              </div>
              <h3 className="inst-why-card-title">Client and company money in separate accounts</h3>
              <p className="inst-why-card-desc">
                Client AED is held in a dedicated client account with our partner bank. AGCX's own operating funds sit in separate company accounts, and the two are never mixed. Your auditors can trace client balances directly, without untangling them from ours.
              </p>
            </div>

            {/* CARD 2 */}
            <div className="inst-why-card">
              <img src="/images/infrastructure-img/why_cards_back.png" alt="" className="inst-why-card-pattern" />
              <div className="inst-why-badge-wrap">
                <img
                  src={isLight ? "/images/infrastructure-img/why_institutions_vector2_light.png" : "/images/infrastructure-img/why_institutions_vector2.png"}
                  alt="Identified Deposits"
                  className="inst-why-vector-img"
                />
              </div>
              <h3 className="inst-why-card-title">Deposits that identify themselves</h3>
              <p className="inst-why-card-desc">
                Your IBAN is unique to your entity. Funds sent to it are matched to your AGCX account automatically: no reference codes, no suspense items, no operations time spent chasing unidentified deposits.
              </p>
            </div>

            {/* CARD 3 */}
            <div className="inst-why-card">
              <img src="/images/infrastructure-img/why_cards_back.png" alt="" className="inst-why-card-pattern" />
              <div className="inst-why-badge-wrap">
                <img
                  src={isLight ? "/images/infrastructure-img/why_institutions_vector3_light.png" : "/images/infrastructure-img/why_institutions_vector3.png"}
                  alt="Same-day settlement"
                  className="inst-why-vector-img"
                />
              </div>
              <h3 className="inst-why-card-title">Same-day settlement, weekends included</h3>
              <p className="inst-why-card-desc">
                AED withdrawals requested before cut-off GST settle to your linked bank account the same day, seven days a week. Requests after the cut-off settle the next day.
              </p>
            </div>

            {/* CARD 4 */}
            <div className="inst-why-card">
              <img src="/images/infrastructure-img/why_cards_back.png" alt="" className="inst-why-card-pattern" />
              <div className="inst-why-badge-wrap">
                <img
                  src={isLight ? "/images/infrastructure-img/why_institutions_vector4_light.png" : "/images/infrastructure-img/why_institutions_vector4.png"}
                  alt="Free, with no minimums"
                  className="inst-why-vector-img"
                />
              </div>
              <h3 className="inst-why-card-title">Free, with no minimums</h3>
              <p className="inst-why-card-desc">
                No issuance fee, no maintenance fee, no minimum balance. The IBAN is part of onboarding, not a product tier you pay to unlock.
              </p>
            </div>

            {/* CARD 5 */}
            <div className="inst-why-card">
              <img src="/images/infrastructure-img/why_cards_back.png" alt="" className="inst-why-card-pattern" />
              <div className="inst-why-badge-wrap">
                <img
                  src={isLight ? "/images/infrastructure-img/why_institutions_vector5_light.png" : "/images/infrastructure-img/why_institutions_vector5.png"}
                  alt="Dollar-stable fiat leg"
                  className="inst-why-vector-img"
                />
              </div>
              <h3 className="inst-why-card-title">A dollar-stable fiat leg</h3>
              <p className="inst-why-card-desc">
                Settling the fiat leg in AED keeps your exposure dollar-linked without a US banking relationship, and removes the FX risk of routing through a third currency.
              </p>
            </div>

            {/* CARD 6 */}
            <div className="inst-why-card">
              <img src="/images/infrastructure-img/why_cards_back.png" alt="" className="inst-why-card-pattern" />
              <div className="inst-why-badge-wrap">
                <img
                  src={isLight ? "/images/infrastructure-img/why_institutions_vector6_light.png" : "/images/infrastructure-img/why_institutions_vector6.png"}
                  alt="Quick onboarding"
                  className="inst-why-vector-img"
                />
              </div>
              <h3 className="inst-why-card-title">Quick onboarding for your traders</h3>
              <p className="inst-why-card-desc">
                Your entity completes KYB with our institutional team. Individual traders under it go through automated KYC and are typically verified in minutes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. BUILT TO PASS YOUR DUE DILIGENCE SECTION */}
      <section
        id="due-diligence"
        className="inst-due-section"
      >
        <div className="inst-due-container">
          {/* Header Row: Title & Subtitle + Illustration */}
          <div className="inst-due-header-grid">
            <div className="inst-due-text-col">
              <h2 className="inst-due-title">
                Built to pass your <span className="inst-due-gold">due diligence.</span>
              </h2>
              <p className="inst-due-subtitle">
                From regulatory licensing to cold storage, our infrastructure is designed for transparency, security and full auditability — so you can meet your own compliance requirements with confidence.
              </p>
            </div>

            <div className="inst-due-graphic-wrap">
              <img
                src="/images/infrastructure-img/diligence_built_img.png"
                alt="Due Diligence Compliance"
                className="inst-due-svg"
              />
            </div>
          </div>

          {/* Cards Row (5 Cards) */}
          <div className="inst-due-cards-grid">
            {/* Card 1 */}
            <div className="inst-due-card">
              <div className="cards_buitseffect">
                <div className="inst-due-icon-badge">
                  <img src="/images/infrastructure-img/diligence_list_icon.png" alt="Travel Rule" className="inst-due-icon-img" />
                </div>
                <h3 className="inst-due-card-title">Travel Rule compliant</h3>
                <p className="inst-due-card-desc">
                  Originator and beneficiary details travel with every qualifying transfer, giving counterparties the compliance data they need.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="inst-due-card">
              <div className="cards_buitseffect">
                <div className="inst-due-icon-badge">
                  <img src="/images/infrastructure-img/diligence_list_icon2.png" alt="Cold Storage" className="inst-due-icon-img" />
                </div>
                <h3 className="inst-due-card-title">90% in cold storage</h3>
                <p className="inst-due-card-desc">
                  Client assets are held by an independent UAE-approved custodian, with 90% kept offline in cold storage.
                </p>
              </div>
            </div>

            {/* Card 3 (Featured Golden Card) */}
            <div className="inst-due-card inst-due-card-featured">
              <div className="cards_buitseffect">
                <div className="inst-due-icon-badge inst-due-icon-badge-gold">
                  <img src="/images/infrastructure-img/diligence_list_icon3.png" alt="Audit Trail" className="inst-due-icon-img" />
                </div>
                <h3 className="inst-due-card-title">Full audit trail</h3>
                <p className="inst-due-card-desc">
                  Every deposit and withdrawal, fiat and crypto, is logged end to end and tied to the client who made it.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="inst-due-card">
              <div className="cards_buitseffect">
                <div className="inst-due-icon-badge">
                  <img src="/images/infrastructure-img/diligence_list_icon4.png" alt="Separate Accounts" className="inst-due-icon-img" />
                </div>
                <h3 className="inst-due-card-title">Separate accounts</h3>
                <p className="inst-due-card-desc">
                  Client money and company money are held in different accounts, so reconciliation and audits start from a clean line.
                </p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="inst-due-card">
              <div className="cards_buitseffect">
                <div className="inst-due-icon-badge">
                  <img src="/images/infrastructure-img/diligence_list_icon5.png" alt="Federally Licensed" className="inst-due-icon-img" />
                </div>
                <h3 className="inst-due-card-title">Federally licensed</h3>
                <p className="inst-due-card-desc">
                  Licensed by the UAE Capital Market Authority (SCA), licence no. 20200000447.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. INSTITUTIONAL SOLUTIONS FOR EVERY MARKET SECTION */}
      <section id="institutional-solutions" className="inst-sol-section">
        <div className="inst-sol-container">
          <div className="inst-sol-grid">
            {/* Left Column: Graphic Image */}
            <div className="inst-sol-graphic-col">
              <div className="inst-sol-graphic-wrap">
                <img
                  src={isLight ? "/images/infrastructure-img/institutional_img_light.png" : "/images/infrastructure-img/institutional_img.png"}
                  alt="Institutional Solutions"
                  className="inst-sol-img"
                />
              </div>
            </div>

            {/* Right Column: Title & Table */}
            <div className="inst-sol-text-col">
              <h2 className="inst-sol-title">
                Institutional solutions <br />
                for <span className="inst-sol-gold">every market.</span>
              </h2>

              {/* Table */}
              <div className="inst-sol-table">
                {/* Header Row */}
                <div className="inst-sol-table-header">
                  <div className="inst-sol-th-left">Institution</div>
                  <div className="inst-sol-th-right">What AGCX solves</div>
                </div>

                {/* Row 1 */}
                <div className="inst-sol-row">
                  <div className="inst-sol-cell-institution">
                    <div className="inst-sol-icon-badge">
                      <img src="/images/infrastructure-img/institutional_list_icon.png" alt="Funds" className="inst-sol-icon-img light_img" />
                      <img src="/images/infrastructure-img/institutional_list_icon_light.png" alt="Funds" className="inst-sol-icon-img dark_img" />
                    </div>
                    <span className="inst-sol-inst-name">Funds and family offices</span>
                  </div>
                  <div className="inst-sol-cell-desc">
                    A clean, auditable fiat leg for allocations into and out of digital assets.
                  </div>
                </div>

                {/* Row 2 */}
                <div className="inst-sol-row">
                  <div className="inst-sol-cell-institution">
                    <div className="inst-sol-icon-badge">
                      <img src="/images/infrastructure-img/institutional_list_icon2.png" alt="OTC desks" className="inst-sol-icon-img light_img" />
                      <img src="/images/infrastructure-img/institutional_list_icon2_light.png" alt="OTC desks" className="inst-sol-icon-img dark_img" />
                    </div>
                    <span className="inst-sol-inst-name">OTC desks and brokers</span>
                  </div>
                  <div className="inst-sol-cell-desc">
                    Travel Rule-compliant transfers and automatic identification of incoming settlement.
                  </div>
                </div>

                {/* Row 3 */}
                <div className="inst-sol-row">
                  <div className="inst-sol-cell-institution">
                    <div className="inst-sol-icon-badge">
                      <img src="/images/infrastructure-img/institutional_list_icon3.png" alt="Payment firms" className="inst-sol-icon-img light_img" />
                      <img src="/images/infrastructure-img/institutional_list_icon3_light.png" alt="Payment firms" className="inst-sol-icon-img dark_img" />
                    </div>
                    <span className="inst-sol-inst-name">Payment and remittance firms</span>
                  </div>
                  <div className="inst-sol-cell-desc">
                    AED funding rails with same-day settlement, paired with crypto liquidity on one venue.
                  </div>
                </div>

                {/* Row 4 */}
                <div className="inst-sol-row">
                  <div className="inst-sol-cell-institution">
                    <div className="inst-sol-icon-badge">
                      <img src="/images/infrastructure-img/institutional_list_icon4.png" alt="Corporates" className="inst-sol-icon-img light_img" />
                      <img src="/images/infrastructure-img/institutional_list_icon4_light.png" alt="Corporates" className="inst-sol-icon-img dark_img" />
                    </div>
                    <span className="inst-sol-inst-name">Corporates and treasuries</span>
                  </div>
                  <div className="inst-sol-cell-desc">
                    Dirham-denominated exposure without a second banking relationship.
                  </div>
                </div>

                {/* Row 5 */}
                <div className="inst-sol-row">
                  <div className="inst-sol-cell-institution">
                    <div className="inst-sol-icon-badge">
                      <img src="/images/infrastructure-img/institutional_list_icon5.png" alt="Exchanges" className="inst-sol-icon-img light_img" />
                      <img src="/images/infrastructure-img/institutional_list_icon5_light.png" alt="Exchanges" className="inst-sol-icon-img dark_img" />
                    </div>
                    <span className="inst-sol-inst-name">Exchanges and platforms</span>
                  </div>
                  <div className="inst-sol-cell-desc">
                    An AED on-ramp for your own users without building banking integrations.
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Process Banner: From first call to first settlement */}
          <div className="inst-proc-banner">
            <div className="inst-proc-left">
              <h3 className="inst-proc-title">
                From first call to <br />
                <span className="inst-proc-gold">first settlement.</span>
              </h3>
              <p className="inst-proc-desc">
                A simple, focused process to get your institution set up — with the right account structure, compliance checks and dedicated support.
              </p>
            </div>

            <div className="inst-proc-steps">
              {/* Step 1 */}
              <div className="inst-proc-step">
                <div className="inst-proc-icon-wrap">
                  <div className="inst-proc-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d4a460" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div className="inst-proc-connector" />
                </div>
                <h4 className="inst-proc-step-title">1. Introduction</h4>
                <p className="inst-proc-step-desc">
                  A call with the institutional desk to scope volumes, assets and account structure.
                </p>
              </div>

              {/* Step 2 */}
              <div className="inst-proc-step">
                <div className="inst-proc-icon-wrap">
                  <div className="inst-proc-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d4a460" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <line x1="10" y1="9" x2="8" y2="9" />
                    </svg>
                  </div>
                  <div className="inst-proc-connector" />
                </div>
                <h4 className="inst-proc-step-title">2. KYB</h4>
                <p className="inst-proc-step-desc">
                  Entity documents, ownership, source of funds.
                </p>
              </div>

              {/* Step 3 */}
              <div className="inst-proc-step">
                <div className="inst-proc-icon-wrap">
                  <div className="inst-proc-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d4a460" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 21h18" />
                      <path d="M3 10h18" />
                      <path d="M5 6l7-3 7 3" />
                      <path d="M4 10v11" />
                      <path d="M20 10v11" />
                      <path d="M8 10v11" />
                      <path d="M12 10v11" />
                      <path d="M16 10v11" />
                    </svg>
                  </div>
                  <div className="inst-proc-connector" />
                </div>
                <h4 className="inst-proc-step-title">3. IBAN assigned</h4>
                <p className="inst-proc-step-desc">
                  Link your bank account and your dedicated AED deposit IBAN is live, at no charge.
                </p>
              </div>

              {/* Step 4 */}
              <div className="inst-proc-step">
                <div className="inst-proc-icon-wrap">
                  <div className="inst-proc-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d4a460" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                    </svg>
                  </div>
                </div>
                <h4 className="inst-proc-step-title">4. Fund and trade</h4>
                <p className="inst-proc-step-desc">
                  Send AED to your IBAN, it's credited automatically, and you're settling the same day.
                </p>
              </div>
            </div>
          </div>

          {/* Institutional Settlement CTA Box with Arch Glow */}
          <div className="inst-cta-box">
            <div className="inst-cta-arch" />
            <div className="inst-cta-content">
              <h3 className="inst-cta-title">
                Let’s talk about your <br />
                settlement flow.
              </h3>
              <p className="inst-cta-subtitle">
                Tell us how your money moves today and we’ll show you what it looks like on our rails.
              </p>
              <div className="inst-cta-btns">
                <button className="inst-cta-btn-primary" onClick={() => navigate("/signup")}>
                  Talk to the institutional desk
                </button>
                <button className="inst-cta-btn-secondary" onClick={() => navigate("/contact")}>
                  Request onboarding pack
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
